import React, { useEffect, useMemo, useState } from "react";
import { Search, Filter, User, Mail, Shield, Building2, ChevronRight, Eye, Edit, Trash2, UserPlus, Download, X, Save, Globe, Calendar, CheckCircle2, Lock as LucideLock, EyeOff, Phone, Users, GraduationCap, Briefcase } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import "../../styles/UtopiaPages.css";

interface UserRecord {
    id: number;
    name: string;
    email: string;
    phone?: string;
    role: string;
    tenant_id: number;
    school_name: string;
    created_at: string;
}

const ROLE_FILTER_OPTIONS = [
    { key: "all", label: "All Users", accent: "bg-slate-900 text-white border-slate-900", Icon: Users },
    { key: "superadmin", label: "Super Admins", accent: "bg-purple-50 text-purple-700 border-purple-200", Icon: Shield },
    { key: "admin", label: "School Admins", accent: "bg-blue-50 text-blue-700 border-blue-200", Icon: Building2 },
    { key: "headteacher", label: "Headteachers", accent: "bg-teal-50 text-teal-700 border-teal-200", Icon: Briefcase },
    { key: "teacher", label: "Teachers", accent: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: User },
    { key: "student", label: "Learners", accent: "bg-amber-50 text-amber-700 border-amber-200", Icon: GraduationCap }
] as const;

const GlobalUsersManagement: React.FC = () => {
    const [users, setUsers] = useState<UserRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [schoolFilter, setSchoolFilter] = useState("all");
    const [schools, setSchools] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    // Modal States
    const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        name: "",
        email: "",
        phone: "",
        role: "",
        tenant_id: 0,
        password: ""
    });
    const [createForm, setCreateForm] = useState({
        name: "",
        title: "",
        email: "",
        phone: "",
        role: "teacher",
        tenant_id: "",
        password: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const token = localStorage.getItem("token") || "";

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, roleFilter, schoolFilter]);

    useEffect(() => {
        fetchSchools();
    }, []);

    const roleCounts = useMemo(() => {
        const counts: Record<string, number> = { all: users.length };
        users.forEach((user) => {
            const key = String(user.role || "").toLowerCase();
            counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [users]);

    const fetchSchools = async () => {
        try {
            const response = await axios.get(getApiUrl("superadmin/schools"), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSchools(response.data || []);
        } catch (err: any) {
            console.error("Failed to load schools", err);
        }
    };

    const fetchUsers = async (page: number) => {
        try {
            setLoading(true);
            const response = await axios.get(getApiUrl("superadmin/users"), {
                params: {
                    page,
                    limit: pagination.limit,
                    search: searchTerm,
                    role: roleFilter,
                    tenant_id: schoolFilter
                },
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data.users || []);
            setPagination(response.data.pagination);
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to load user data");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchUsers(newPage);
        }
    };

    const handleViewUser = (user: UserRecord) => {
        setSelectedUser(user);
        setIsViewModalOpen(true);
    };

    const handleEditUser = (user: UserRecord) => {
        setSelectedUser(user);
        setEditForm({
            name: user.name,
            email: user.email,
            phone: user.phone || "",
            role: user.role,
            tenant_id: user.tenant_id,
            password: ""
        });
        setIsEditModalOpen(true);
    };

    const handleUpdateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) return;

        try {
            setIsUpdating(true);
            const payload = { ...editForm };
            if (!payload.password) delete (payload as any).password;

            await axios.patch(getApiUrl(`superadmin/users/${selectedUser.id}`), payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsEditModalOpen(false);
            fetchUsers(pagination.page);
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to update user");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsCreating(true);
            const payload: any = {
                name: createForm.name,
                title: createForm.title,
                email: createForm.email,
                phone: createForm.phone,
                role: createForm.role,
                password: createForm.password
            };
            if (createForm.role !== "superadmin") {
                payload.tenant_id = createForm.tenant_id;
            }
            await axios.post(getApiUrl("superadmin/users"), payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsCreateModalOpen(false);
            setCreateForm({ name: "", title: "", email: "", phone: "", role: "teacher", tenant_id: "", password: "" });
            fetchUsers(1);
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to create user");
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteUser = async (user: UserRecord) => {
        const confirmDelete = window.confirm(`Delete ${user.name} (${user.email})? This cannot be undone.`);
        if (!confirmDelete) return;
        try {
            await axios.delete(getApiUrl(`superadmin/users/${user.id}`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchUsers(pagination.page);
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to delete user");
        }
    };

    const getRoleBadge = (role: string) => {
        const configs: Record<string, string> = {
            superadmin: "bg-purple-50 text-purple-700 border-purple-100",
            admin: "bg-blue-50 text-blue-700 border-blue-100",
            headteacher: "bg-teal-50 text-teal-700 border-teal-100",
            teacher: "bg-indigo-50 text-indigo-700 border-indigo-100",
            student: "bg-amber-50 text-amber-700 border-amber-100"
        };
        return (
            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${configs[role] || "bg-slate-50 text-slate-700 border-slate-100"}`}>
                {role}
            </span>
        );
    };

    if (loading && !users.length) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-50/30">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-400 font-black text-[10px] uppercase tracking-widest">Scanning Global Directory...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <main className="dashboard-main p-8 bg-slate-50/30">
                <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                                <Shield size={18} className="text-white" />
                            </div>
                            <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] bg-indigo-50 px-3 py-1 rounded-full">Identity Management</span>
                        </div>
                        <h1 className="text-4xl font-black text-slate-800 tracking-tight">Global <span className="text-indigo-500 italic">Users</span></h1>
                        <p className="text-slate-400 mt-2 font-medium uppercase text-[11px] tracking-widest">Managing {pagination.total} unique identities across platform</p>
                    </div>
                    <div className="flex gap-3">
                        <button className="flex items-center gap-2 bg-white border border-slate-200 px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all">
                            <Download size={16} /> Export CSV
                        </button>
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/20"
                        >
                            <UserPlus size={16} /> Provision User
                        </button>
                    </div>
                </header>

                {error && (
                    <div className="bg-rose-50 border border-rose-200 p-4 rounded-2xl text-rose-700 font-bold mb-8 text-sm flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></div>
                        {error}
                        <button onClick={() => setError("")} className="ml-auto text-rose-400 hover:text-rose-600"><X size={16} /></button>
                    </div>
                )}

                <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
                    <div className="px-6 pt-6">
                        <div className="flex flex-wrap gap-3">
                            {ROLE_FILTER_OPTIONS.map(({ key, label, accent, Icon }) => {
                                const active = roleFilter === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setRoleFilter(key)}
                                        className={`inline-flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${active ? accent : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"}`}
                                    >
                                        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${active ? "bg-white/15" : "bg-slate-100 text-slate-600"}`}>
                                            <Icon size={16} />
                                        </span>
                                        <span>
                                            <span className="block text-[10px] font-black uppercase tracking-[0.18em]">{label}</span>
                                            <span className={`mt-1 block text-xs font-semibold ${active ? "text-inherit" : "text-slate-400"}`}>
                                                {roleCounts[key] || 0} in current view
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="p-6 border-b border-slate-50 bg-slate-50/50 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="relative md:col-span-2 group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                            <input
                                type="text"
                                placeholder="Search by name, email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500/30 outline-none transition-all shadow-sm"
                            />
                        </div>

                        <div className="relative">
                            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                className="w-full pl-11 pr-8 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest focus:ring-4 focus:ring-indigo-500/5 outline-none appearance-none transition-all cursor-pointer shadow-sm"
                            >
                                <option value="all">Global Roles</option>
                                <option value="superadmin">Super Admins</option>
                                <option value="admin">School Admins</option>
                                <option value="headteacher">Headteachers</option>
                                <option value="teacher">Teachers</option>
                                <option value="student">Students</option>
                            </select>
                        </div>

                        <div className="relative">
                            <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <select
                                value={schoolFilter}
                                onChange={(e) => setSchoolFilter(e.target.value)}
                                className="w-full pl-11 pr-8 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest focus:ring-4 focus:ring-indigo-500/5 outline-none appearance-none transition-all cursor-pointer shadow-sm"
                            >
                                <option value="all">All Ecosystems</option>
                                {schools.map(school => (
                                    <option key={school.id} value={school.id}>{school.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/30 border-b border-slate-50">
                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">User Profile</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Privilege Level</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Associated Institute</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Deployment Date</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {users.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-16 h-16 rounded-[2rem] bg-indigo-50 flex items-center justify-center">
                                                    <Search size={24} className="text-indigo-200" />
                                                </div>
                                                <p className="text-sm font-black text-slate-300 uppercase tracking-widest">Identity lookup failed - No matches</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id} className="group hover:bg-slate-50/50 transition-all">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 shadow-inner group-hover:bg-indigo-500 group-hover:text-white transition-all">
                                                        <User size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-black text-slate-800 tracking-tight">{user.name}</div>
                                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 mt-0.5">
                                                            <Mail size={12} className="text-slate-300" /> {user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                {getRoleBadge(user.role)}
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-400">
                                                        {user.school_name ? user.school_name[0] : "?"}
                                                    </div>
                                                    <span className="text-xs font-bold text-slate-600 tracking-tight italic">
                                                        {user.school_name || "Platform Global"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    {new Date(user.created_at).toLocaleDateString()}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <div className="flex justify-end gap-2 opacity-30 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => handleViewUser(user)}
                                                        className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-all"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditUser(user)}
                                                        className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-teal-50 hover:text-teal-600 transition-all"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user)}
                                                        className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="p-6 bg-slate-50/50 border-t border-slate-50 flex items-center justify-between">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            Showing {users.length} of {pagination.total} Identities
                        </p>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1 || loading}
                                className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase text-slate-500 hover:bg-slate-50 transition-all disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">
                                Global Frame {pagination.page} / {pagination.totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.totalPages || loading}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase shadow-lg shadow-indigo-600/10 disabled:opacity-50"
                            >
                                Next Stage
                            </button>
                        </div>
                    </div>
                </div>

                {/* View Modal */}
                {isViewModalOpen && selectedUser && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                        <div className="bg-white w-full max-w-xl rounded-[3rem] shadow-3xl overflow-hidden animate-in fade-in zoom-in duration-300">
                            <div className="h-32 bg-gradient-to-br from-indigo-500 to-purple-600 relative">
                                <button
                                    onClick={() => setIsViewModalOpen(false)}
                                    className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-all"
                                >
                                    <X size={20} />
                                </button>
                                <div className="absolute -bottom-12 left-10 w-24 h-24 rounded-[2rem] bg-white p-2 shadow-2xl">
                                    <div className="w-full h-full rounded-[1.5rem] bg-indigo-50 flex items-center justify-center text-indigo-500">
                                        <User size={40} />
                                    </div>
                                </div>
                            </div>
                            <div className="pt-16 px-10 pb-10">
                                <div className="flex items-center justify-between mb-8">
                                    <div>
                                        <h2 className="text-2xl font-black text-slate-900">{selectedUser.name}</h2>
                                        <p className="text-slate-400 font-bold text-sm tracking-tight italic">{selectedUser.email}</p>
                                        {selectedUser.phone && (
                                            <p className="text-slate-400 font-bold text-xs tracking-tight">{selectedUser.phone}</p>
                                        )}
                                    </div>
                                    {getRoleBadge(selectedUser.role)}
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="p-5 rounded-3xl bg-slate-50 border border-slate-100">
                                        <div className="flex items-center gap-3 mb-2 text-indigo-500">
                                            <Building2 size={16} />
                                            <span className="text-[10px] font-black uppercase tracking-widest">Institute</span>
                                        </div>
                                        <p className="text-sm font-bold text-slate-700">{selectedUser.school_name || "Platform Global"}</p>
                                    </div>
                                    <div className="p-5 rounded-3xl bg-slate-50 border border-slate-100">
                                        <div className="flex items-center gap-3 mb-2 text-purple-500">
                                            <Calendar size={16} />
                                            <span className="text-[10px] font-black uppercase tracking-widest">Joined</span>
                                        </div>
                                        <p className="text-sm font-bold text-slate-700">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="p-5 rounded-3xl bg-slate-50 border border-slate-100">
                                        <div className="flex items-center gap-3 mb-2 text-sky-500">
                                            <Phone size={16} />
                                            <span className="text-[10px] font-black uppercase tracking-widest">Phone</span>
                                        </div>
                                        <p className="text-sm font-bold text-slate-700">{selectedUser.phone || "N/A"}</p>
                                    </div>
                                    <div className="p-5 rounded-3xl bg-slate-50 border border-slate-100 col-span-2">
                                        <div className="flex items-center gap-3 mb-2 text-teal-500">
                                            <Shield size={16} />
                                            <span className="text-[10px] font-black uppercase tracking-widest">Security ID</span>
                                        </div>
                                        <p className="text-sm font-mono font-bold text-slate-700 select-all">#UID-{selectedUser.id.toString().padStart(6, '0')}</p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => {
                                        setIsViewModalOpen(false);
                                        handleEditUser(selectedUser);
                                    }}
                                    className="w-full mt-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-slate-900/20 hover:scale-[1.02] active:scale-95 transition-all"
                                >
                                    Modify Identity Parameters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Modal */}
                {isCreateModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                        <div className="bg-white w-full max-w-xl rounded-[2.5rem] shadow-3xl overflow-hidden animate-in fade-in zoom-in duration-300">
                            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-black text-slate-800">Provision User</h2>
                                    <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mt-1">Create any role across schools</p>
                                </div>
                                <button
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-all"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <form className="p-6 space-y-4" onSubmit={handleCreateUser}>
                                <div>
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">Full Name</label>
                                    <input
                                        type="text"
                                        value={createForm.name}
                                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">Title</label>
                                    <input
                                        type="text"
                                        list="global-user-title-options"
                                        value={createForm.title}
                                        onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 outline-none"
                                        required={createForm.role !== "student"}
                                    />
                                    <datalist id="global-user-title-options">
                                        <option value="Mr" />
                                        <option value="Ms" />
                                        <option value="Mrs" />
                                        <option value="Miss" />
                                        <option value="Dr" />
                                        <option value="Prof" />
                                        <option value="Rev" />
                                    </datalist>
                                </div>
                                <div>
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">Email</label>
                                    <input
                                        type="email"
                                        value={createForm.email}
                                        onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">Phone</label>
                                    <input
                                        type="tel"
                                        value={createForm.phone}
                                        onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 outline-none"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-black uppercase tracking-widest text-slate-400">Role</label>
                                        <select
                                            value={createForm.role}
                                            onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                                            className="mt-2 w-full px-4 py-3 rounded-2xl border border-slate-200 text-[11px] font-black uppercase tracking-widest focus:ring-4 focus:ring-indigo-500/10 outline-none"
                                        >
                                            <option value="superadmin">Super Admin</option>
                                            <option value="admin">Admin</option>
                                            <option value="headteacher">Headteacher</option>
                                            <option value="teacher">Teacher</option>
                                            <option value="student">Student</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-black uppercase tracking-widest text-slate-400">School</label>
                                        <select
                                            value={createForm.tenant_id}
                                            onChange={(e) => setCreateForm({ ...createForm, tenant_id: e.target.value })}
                                            disabled={createForm.role === "superadmin"}
                                            className="mt-2 w-full px-4 py-3 rounded-2xl border border-slate-200 text-[11px] font-black uppercase tracking-widest focus:ring-4 focus:ring-indigo-500/10 outline-none disabled:opacity-60"
                                        >
                                            <option value="">Select School</option>
                                            {schools.map((school) => (
                                                <option key={school.id} value={school.id}>
                                                    {school.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">Password</label>
                                    <div className="mt-2 relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={createForm.password}
                                            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                            className="w-full px-4 py-3 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-4 focus:ring-indigo-500/10 outline-none pr-12"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>
                                <div className="pt-4 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsCreateModalOpen(false)}
                                        className="px-5 py-3 rounded-2xl border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isCreating}
                                        className="px-6 py-3 rounded-2xl bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-600/20 disabled:opacity-60"
                                    >
                                        {isCreating ? "Creating..." : "Create User"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Edit Modal */}
                {isEditModalOpen && selectedUser && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                        <div className="bg-white w-full max-w-xl rounded-[3rem] shadow-3xl overflow-hidden animate-in slide-in-from-bottom duration-300">
                            <div className="p-8 border-b border-slate-50 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center">
                                        <Edit size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-slate-900">Modify <span className="text-teal-600">Identity</span></h2>
                                        <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">Configuring global user record</p>
                                    </div>
                                </div>
                                <button onClick={() => setIsEditModalOpen(false)} className="w-10 h-10 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-slate-100">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleUpdateUser} className="p-8 space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Full Name</label>
                                    <div className="relative">
                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                                        <input
                                            type="text"
                                            required
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-700 focus:bg-white focus:border-teal-500/30 outline-none transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                                        <input
                                            type="email"
                                            required
                                            value={editForm.email}
                                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                            className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-700 focus:bg-white focus:border-teal-500/30 outline-none transition-all"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Phone Number</label>
                                    <div className="relative">
                                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                                        <input
                                            type="tel"
                                            required
                                            value={editForm.phone}
                                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                            className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-700 focus:bg-white focus:border-teal-500/30 outline-none transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Global Role</label>
                                        <select
                                            required
                                            value={editForm.role}
                                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                            className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-700 focus:bg-white focus:border-teal-500/30 outline-none cursor-pointer"
                                        >
                                            <option value="superadmin">Super Admin</option>
                                            <option value="admin">School Admin</option>
                                            <option value="headteacher">Headteacher</option>
                                            <option value="teacher">Teacher</option>
                                            <option value="student">Student</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Institute Entity</label>
                                        <select
                                            required
                                            value={editForm.tenant_id}
                                            onChange={(e) => setEditForm({ ...editForm, tenant_id: parseInt(e.target.value) })}
                                            className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-700 focus:bg-white focus:border-teal-500/30 outline-none cursor-pointer"
                                        >
                                            <option value={0}>Network Center (No School)</option>
                                            {schools.map(school => (
                                                <option key={school.id} value={school.id}>{school.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Administrative Password Override (Optional)</label>
                                    <div className="relative">
                                        <LucideLock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={editForm.password}
                                            onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                                            placeholder="Leave blank to keep current password"
                                            className="w-full pl-11 pr-12 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-700 focus:bg-white focus:border-teal-500/30 outline-none transition-all placeholder:text-slate-300 placeholder:font-normal"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-teal-500 transition-colors"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="pt-4 flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsEditModalOpen(false)}
                                        className="flex-1 py-4 bg-slate-50 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-all"
                                    >
                                        Abort
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isUpdating}
                                        className="flex-[2] py-4 bg-teal-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-teal-600/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                                    >
                                        {isUpdating ? (
                                            <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                        ) : (
                                            <Save size={14} />
                                        )}
                                        {isUpdating ? 'Synchronizing...' : 'Commit Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default GlobalUsersManagement;
